import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useActivityLog } from "@/hooks/useActivityLog";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "login" | "signup";
}

type ModalMode = "login" | "signup" | "forgot";

export default function AuthModal({ isOpen, onClose, initialMode = "login" }: AuthModalProps) {
  const { signIn, signUp, resetPassword, resendConfirmation, user, emailConfirmed } = useAuth();
  const { logActivity } = useActivityLog();
  const [mode, setMode] = useState<ModalMode>(initialMode);
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [resendStatus, setResendStatus] = useState<"idle" | "loading" | "sent">("idle");
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setFormStatus("idle");
      setFormError("");
      setResendStatus("idle");
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (user && isOpen) {
      if (emailConfirmed) {
        setFormStatus("success");
        logActivity("login", { timestamp: new Date().toISOString() });
        setTimeout(() => {
          setFormStatus("idle");
          onClose();
        }, 800);
      } else {
        setFormStatus("idle");
        setFormError("");
      }
    }
  }, [user, emailConfirmed, isOpen, onClose, logActivity]);

  if (!isOpen) return null;

  const handleResendConfirmation = async () => {
    if (!user?.email) return;
    setResendStatus("loading");
    const { error } = await resendConfirmation(user.email);
    if (error) {
      setResendStatus("idle");
    } else {
      setResendStatus("sent");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setFormError("");
      if (formRef.current) formRef.current.reset();
      setTimeout(() => {
        setFormStatus("idle");
        onClose();
      }, 1500);
      return;
    }

    const email = (formData.get("email") as string) || "";

    setFormStatus("loading");
    setFormError("");

    if (mode === "forgot") {
      const { error } = await resetPassword(email);
      if (error) {
        setFormStatus("error");
        setFormError(error);
      } else {
        setFormStatus("success");
        setFormError("");
        if (formRef.current) formRef.current.reset();
      }
      return;
    }

    const password = (formData.get("password") as string) || "";

    if (mode === "login") {
      const { error } = await signIn(email, password);
      if (error) {
        setFormStatus("error");
        setFormError(error);
      }
    } else {
      const username = (formData.get("username") as string) || "";
      const confirmPassword = (formData.get("confirm_password") as string) || "";

      if (password !== confirmPassword) {
        setFormStatus("error");
        setFormError("Passwords do not match.");
        return;
      }

      if (password.length < 6) {
        setFormStatus("error");
        setFormError("Password must be at least 6 characters.");
        return;
      }

      const { error } = await signUp(email, password, username);
      if (error) {
        setFormStatus("error");
        setFormError(error);
      } else {
        await logActivity("signup", { timestamp: new Date().toISOString() });
        setFormStatus("success");
        setFormError("");
        if (formRef.current) formRef.current.reset();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-[#161b22] rounded-lg w-full max-w-md mx-4 overflow-hidden shadow-xl">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#1c2128] text-gray-400 transition-colors z-10"
        >
          <i className="fas fa-times" />
        </button>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {mode === "forgot" ? (
            <button
              onClick={() => { setMode("login"); setFormStatus("idle"); setFormError(""); }}
              className="flex-1 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap text-[#5cb85c] border-b-2 border-[#5cb85c]"
            >
              RESET PASSWORD
            </button>
          ) : (
            <>
              <button
                onClick={() => { setMode("login"); setFormStatus("idle"); setFormError(""); }}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                  mode === "login"
                    ? "text-[#5cb85c] border-b-2 border-[#5cb85c]"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                LOGIN
              </button>
              <button
                onClick={() => { setMode("signup"); setFormStatus("idle"); setFormError(""); }}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                  mode === "signup"
                    ? "text-[#5cb85c] border-b-2 border-[#5cb85c]"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                SIGN UP
              </button>
            </>
          )}
        </div>

        {/* Form */}
        <div className="p-6">
          {/* Email Verification Banner — shown when logged in but email not confirmed */}
          {user && !emailConfirmed && (
            <div className="mb-5 p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="fas fa-envelope text-amber-400 text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-400 mb-1">Email not confirmed</p>
                  <p className="text-xs text-amber-400 leading-relaxed mb-3">
                    We sent a confirmation link to <strong>{user.email}</strong>. Please check your inbox and click the link to verify your account.
                  </p>
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendStatus !== "idle"}
                    className="text-xs font-semibold text-amber-400 hover:text-amber-900 underline transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {resendStatus === "loading" ? (
                      <span><i className="fas fa-spinner fa-spin mr-1" />Sending...</span>
                    ) : resendStatus === "sent" ? (
                      <span><i className="fas fa-check mr-1" />Resent! Check your inbox</span>
                    ) : (
                      <span>Resend confirmation email</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="absolute opacity-0 pointer-events-none"
            />

            {mode === "forgot" && (
              <>
                <p className="text-sm text-gray-400 mb-4">
                  Enter your email and we'll send you a password reset link.
                </p>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                  />
                </div>

                {formStatus === "error" && formError && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                    <i className="fas fa-exclamation-circle mr-2" />{formError}
                  </div>
                )}
                {formStatus === "success" && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
                    <i className="fas fa-check-circle mr-2" />Reset link sent! Check your email.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "loading"}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                >
                  {formStatus === "loading" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Sending...</span>
                  ) : "SEND RESET LINK"}
                </button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  <button onClick={() => { setMode("login"); setFormStatus("idle"); setFormError(""); }} className="text-[#5bc0de] hover:text-[#46b8da] font-semibold">Back to Login</button>
                </p>
              </>
            )}

            {mode !== "forgot" && (
              <>
                {mode === "signup" && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Username</label>
                    <input
                      type="text"
                      name="username"
                      required
                      placeholder="Choose a username"
                      className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Password</label>
                  <input
                    type="password"
                    name="password"
                    required
                    placeholder="Enter your password"
                    className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                  />
                </div>

                {mode === "signup" && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-400 mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      name="confirm_password"
                      required
                      placeholder="Confirm your password"
                      className="w-full px-3 py-2.5 border border-gray-600 rounded bg-[#0d1117] text-sm focus:outline-none focus:border-[#5cb85c] transition-colors"
                    />
                  </div>
                )}

                {mode === "login" && (
                  <div className="flex items-center justify-between mb-4">
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" name="remember_me" className="rounded" />
                      <span className="text-xs text-gray-400">Remember me</span>
                    </label>
                    <button type="button" onClick={() => { setMode("forgot"); setFormStatus("idle"); setFormError(""); }} className="text-xs text-[#5bc0de] hover:text-[#46b8da]">Forgot password?</button>
                  </div>
                )}

                {/* Status Messages */}
                {formStatus === "error" && formError && (
                  <div className="mb-4 p-3 bg-red-900/20 border border-red-700/30 rounded text-sm text-red-400">
                    <i className="fas fa-exclamation-circle mr-2" />{formError}
                  </div>
                )}
                {formStatus === "success" && (
                  <div className="mb-4 p-3 bg-green-900/20 border border-green-700/30 rounded text-sm text-green-400">
                    <i className="fas fa-check-circle mr-2" />{mode === "login" ? "Login successful!" : "Account created! Check your email to confirm."}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "loading"}
                  className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded transition-all whitespace-nowrap"
                >
                  {formStatus === "loading" ? (
                    <span><i className="fas fa-spinner fa-spin mr-2" />Please wait...</span>
                  ) : mode === "login" ? "LOGIN" : "CREATE ACCOUNT"}
                </button>
              </>
            )}
          </form>

          {/* Toggle text */}
          <p className="text-xs text-gray-400 text-center mt-4">
            {mode === "login" ? (
              <>Don't have an account?{' '}<button onClick={() => { setMode("signup"); setFormStatus("idle"); setFormError(""); }} className="text-[#5bc0de] hover:text-[#46b8da] font-semibold">Sign Up</button></>
            ) : (
              <>Already have an account?{' '}<button onClick={() => { setMode("login"); setFormStatus("idle"); setFormError(""); }} className="text-[#5bc0de] hover:text-[#46b8da] font-semibold">Login</button></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}